import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { API_BASE } from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import Brand from "../components/Brand";
import {
  Leaf, Users, MapPin, Search, ArrowUpRight, ShieldCheck, Sparkles, Sun, Recycle,
  Mail, ChevronRight, Compass,
} from "lucide-react";

const publicApi = axios.create({ baseURL: API_BASE });

const HERO_IMG = "https://images.unsplash.com/photo-1679486689377-e4fcf6861a67?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHx0b3BvZ3JhcGh5JTIwbWFwJTIwdGV4dHVyZXxlbnwwfHx8fDE3ODE0MTczODN8MA&ixlib=rb-4.1.0&q=85";
const SEEDS_IMG = "https://images.unsplash.com/photo-1774930732190-22067ec6f503?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxuYXRpdmUlMjBwbGFudCUyMHNlZWRzfGVufDB8fHx8MTc4MTQxNzM4Mnww&ixlib=rb-4.1.0&q=85";
const FIELD_IMG = "https://images.unsplash.com/photo-1573757173021-940801303a5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxmaWVsZCUyMHJlc2VhcmNoZXIlMjBvdXRkb29yc3xlbnwwfHx8fDE3ODE0MTczODJ8MA&ixlib=rb-4.1.0&q=85";

function colorForRisk(score) {
  if (score == null) return "#8A857D";
  if (score >= 7) return "#B45B3E";
  if (score >= 4) return "#D08C38";
  return "#4A5D4E";
}

function FitBounds({ points }) {
  const map = useMap();
  React.useEffect(() => {
    if (!points?.length) return;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds.pad(0.25), { maxZoom: 7 });
  }, [points, map]);
  return null;
}

export default function Atlas() {
  const [q, setQ] = useState("");
  const [active, setActive] = useState("");

  const { data: stats } = useQuery({
    queryKey: ["atlas-stats"],
    queryFn: async () => (await publicApi.get("/public/atlas/stats")).data,
  });

  const { data: collections = [] } = useQuery({
    queryKey: ["atlas-collections", q],
    queryFn: async () => (await publicApi.get(`/public/atlas/collections${q ? `?q=${encodeURIComponent(q)}` : ""}`)).data,
  });

  const { data: points = [] } = useQuery({
    queryKey: ["atlas-map"],
    queryFn: async () => (await publicApi.get("/public/atlas/map")).data,
  });

  const filtered = useMemo(() => {
    if (!active) return collections;
    return collections.filter((c) => c.id === active);
  }, [collections, active]);

  const center = points.length ? [points[0].lat, points[0].lng] : [-25.27, 133.77];

  return (
    <div className="min-h-screen bg-background text-[#2C2A28]" data-testid="atlas-page">
      {/* ---------- Top bar ---------- */}
      <header className="absolute top-0 left-0 right-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-6 flex items-center justify-between">
          <Link to="/atlas" className="text-[#FDFBF7]" data-testid="atlas-brand">
            <Brand variant="inline" tone="light" size={44} subtitle="Restoration Atlas" />
          </Link>
          <nav className="flex items-center gap-2 lg:gap-4">
            <a href="#map" className="hidden md:inline text-[12.5px] tracking-[0.18em] uppercase font-semibold text-[#FDFBF7]/90 hover:text-[#FDFBF7]" data-testid="atlas-nav-map">Map</a>
            <a href="#collections" className="hidden md:inline text-[12.5px] tracking-[0.18em] uppercase font-semibold text-[#FDFBF7]/90 hover:text-[#FDFBF7]" data-testid="atlas-nav-collections">Collections</a>
            <a href="#partners" className="hidden md:inline text-[12.5px] tracking-[0.18em] uppercase font-semibold text-[#FDFBF7]/90 hover:text-[#FDFBF7]" data-testid="atlas-nav-partners">Partner</a>
            <Link to="/login" className="text-[12.5px] tracking-[0.18em] uppercase font-semibold text-[#FDFBF7]/90 hover:text-[#FDFBF7] ml-2 lg:ml-4" data-testid="atlas-nav-login">Sign in</Link>
            <Link to="/register" data-testid="atlas-nav-register">
              <Button className="bg-[#FDFBF7] text-[#2C2A28] hover:bg-[#F4F1EA] gap-1.5">
                Get access <ArrowUpRight className="w-3.5 h-3.5" />
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* ---------- Hero ---------- */}
      <section className="relative min-h-[720px] flex items-end overflow-hidden">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#2C2A28]/88 via-[#5C5854]/60 to-[#8B5A2B]/75" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 pt-32 pb-20 grid lg:grid-cols-[1.5fr_1fr] gap-10 items-end w-full">
          <div className="text-[#FDFBF7] max-w-3xl">
            <div className="flex items-baseline gap-3 flex-wrap">
              <span className="font-display text-[64px] sm:text-[80px] lg:text-[96px] leading-none tracking-[0.16em] font-bold text-[#FDFBF7]" data-testid="hero-acronym">SPIRIT</span>
              <span className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[#FDFBF7]/80 pb-2">An acronym for</span>
            </div>
            <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] font-medium tracking-tight mt-5">
              Seed Provenance Intelligence and Restoration Information Technology
            </h1>
            <p className="mt-7 text-[15px] lg:text-[16px] leading-relaxed text-[#FDFBF7]/90 max-w-2xl">
              An integrated platform for the collection, management and analysis of ecological, genetic,
              cultural and climate datasets associated with native seed resources.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <a href="#domains" data-testid="atlas-hero-domains">
                <Button className="bg-[#FDFBF7] text-[#2C2A28] hover:bg-[#F4F1EA] gap-2 px-6">
                  <Compass className="w-4 h-4" strokeWidth={1.8} /> Explore the framework
                </Button>
              </a>
              <a href="#partners" data-testid="atlas-hero-partner">
                <Button variant="outline" className="bg-transparent border-white/30 text-[#FDFBF7] hover:bg-white/10 hover:text-[#FDFBF7] gap-2 px-6">
                  Partner with us <ChevronRight className="w-4 h-4" />
                </Button>
              </a>
            </div>
          </div>

          <div className="text-[#FDFBF7]/90">
            <div className="grid grid-cols-2 gap-x-6 gap-y-7 border-t border-white/20 pt-7">
              <Stat label="Public provenances" value={stats?.total_collections} />
              <Stat label="Native species" value={stats?.species_recorded} />
              <Stat label="Traditional Owner groups" value={stats?.to_groups_involved} />
              <Stat label="Organisations involved" value={stats?.organisations_involved} />
              <Stat label="Mapped on Country" value={stats?.mapped_collections} />
              <Stat label="Climate-assessed" value={stats?.climate_assessed} />
            </div>
            <p className="text-[11px] mt-6 opacity-70 italic leading-relaxed border-l-2 border-white/30 pl-4">
              Cultural knowledge is held with care. Sensitive information — elder stories, restricted uses,
              cultural names — is not displayed publicly.
            </p>
          </div>
        </div>
      </section>

      {/* ---------- Four Knowledge Domains ---------- */}
      <section id="domains" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
        <div className="grid lg:grid-cols-[1fr_1.5fr] gap-12 items-start">
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#8B5A2B]">Four integrated knowledge domains</div>
            <h2 className="font-serif-display text-4xl lg:text-5xl leading-[1.05] font-medium tracking-tight mt-3">
              One framework. Four ways of knowing.
            </h2>
            <p className="text-[14.5px] leading-relaxed text-[#5C5854] mt-6">
              SPIRIT integrates these knowledge domains into a single provenance intelligence framework for
              native seed systems — bringing ecological, genetic, cultural and climate data together with
              cultural authority at the centre.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4" data-testid="atlas-knowledge-domains">
            <DomainCard
              title="Ecological Intelligence"
              icon={MapPin}
              items={["Site characteristics", "Species associations", "Environmental conditions"]}
              testid="atlas-domain-ecological"
            />
            <DomainCard
              title="Genetic Intelligence"
              icon={Sparkles}
              items={["Provenance", "Population diversity", "Germplasm information"]}
              testid="atlas-domain-genetic"
            />
            <DomainCard
              title="Cultural Intelligence"
              icon={ShieldCheck}
              items={["Traditional Owner authority", "Cultural permissions", "Knowledge governance"]}
              testid="atlas-domain-cultural"
            />
            <DomainCard
              title="Climate Intelligence"
              icon={Sun}
              items={["Environmental change indicators", "Climate projections", "Adaptation opportunities"]}
              testid="atlas-domain-climate"
            />
          </div>
        </div>
      </section>

      {/* ---------- Map ---------- */}
      <section id="map" className="bg-[#F4F1EA] border-y border-[#E6E2D8] py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#8B5A2B]">On Country</div>
              <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight mt-2">Provenance, mapped</h2>
              <p className="text-[13.5px] text-[#5C5854] mt-2 max-w-xl">Each marker is a public provenance shared by its Traditional Owners.</p>
            </div>
            <div className="flex items-center gap-4 text-[10.5px] tracking-[0.16em] uppercase font-semibold text-[#5C5854]">
              <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#4A5D4E]" /> Low risk</span>
              <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#D08C38]" /> Moderate</span>
              <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#B45B3E]" /> High</span>
              <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#8A857D]" /> Unassessed</span>
            </div>
          </div>

          <Card className="mt-7 border-[#E6E2D8] shadow-sm overflow-hidden bg-white">
            <div className="h-[560px] relative">
              <MapContainer center={center} zoom={4} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
                <TileLayer attribution='© OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <FitBounds points={points} />
                {points.map((p) => (
                  <CircleMarker
                    key={p.id}
                    center={[p.lat, p.lng]}
                    radius={10}
                    pathOptions={{ color: colorForRisk(p.climate_risk_score), weight: 2, fillColor: colorForRisk(p.climate_risk_score), fillOpacity: 0.45 }}
                    eventHandlers={{ click: () => setActive(p.id) }}
                  >
                    <Popup>
                      <div className="min-w-[200px]">
                        <div className="font-mono-data text-[10px] text-[#8A857D]">{p.collection_id}</div>
                        <div className="font-serif-display italic text-[14px] text-[#2C2A28] mt-0.5">{p.species_scientific_name}</div>
                        <div className="text-[11.5px] text-[#5C5854]">{p.species_common_name}</div>
                        <div className="text-[11px] text-[#5C5854] mt-1">TO: {p.to_group || "—"}</div>
                        <a href={`#c-${p.id}`} onClick={() => setActive(p.id)} className="block mt-2 text-[10.5px] tracking-[0.18em] uppercase font-semibold text-[#8B5A2B]">View provenance →</a>
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}
              </MapContainer>
              {points.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="px-5 py-3 rounded-md bg-white/95 border border-[#E6E2D8] text-[12.5px] text-[#5C5854] flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#8B5A2B]" /> No public provenances mapped yet.
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      </section>

      {/* ---------- Collections gallery ---------- */}
      <section id="collections" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#8B5A2B]">Public provenances</div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight mt-2">Browse the atlas</h2>
            <p className="text-[13.5px] text-[#5C5854] mt-2 max-w-xl">Search by species, family or project type. Click any record for the public profile.</p>
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8A857D]" />
            <Input
              value={q}
              onChange={(e) => { setQ(e.target.value); setActive(""); }}
              placeholder="Eucalyptus, Acacia, biodiversity…"
              className="bg-white border-[#E6E2D8] pl-9"
              data-testid="atlas-search"
            />
          </div>
        </div>

        {collections.length === 0 ? (
          <Card className="p-12 mt-8 border-dashed border-[#E6E2D8] bg-transparent text-center">
            <div className="flex justify-center">
              <img src={SEEDS_IMG} alt="" className="w-32 h-32 rounded-md object-cover opacity-90" />
            </div>
            <h3 className="font-display text-xl font-semibold mt-5">The atlas is just beginning.</h3>
            <p className="text-[13px] text-[#5C5854] mt-2 max-w-md mx-auto">
              No public provenances yet. Ranger groups and Traditional Owner organisations decide when a record is
              ready to be shared here. The atlas will grow as permissions are granted.
            </p>
            <Link to="/register" className="inline-block mt-5" data-testid="atlas-empty-cta">
              <Button className="bg-[#8B5A2B] hover:bg-[#6B4420] text-[#FDFBF7] gap-2">Partner with us <ArrowUpRight className="w-4 h-4" /></Button>
            </Link>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8 sk-stagger">
            {filtered.map((c) => (
              <AtlasCard key={c.id} c={c} testid={`atlas-card-${c.id}`} />
            ))}
            {active && filtered.length === 1 && (
              <button
                onClick={() => setActive("")}
                className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[#8B5A2B] underline-offset-4 hover:underline col-span-full"
                data-testid="atlas-clear-active"
              >
                ← Show all
              </button>
            )}
          </div>
        )}
      </section>

      {/* ---------- Partner CTA ---------- */}
      <section id="partners" className="relative overflow-hidden">
        <img src={FIELD_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2C2A28]/90 via-[#4A5D4E]/80 to-[#8B5A2B]/70" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32 grid lg:grid-cols-[1.4fr_1fr] gap-10 text-[#FDFBF7]">
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase font-semibold opacity-90">Partner with SeedKeepers</div>
            <h2 className="font-serif-display text-4xl lg:text-6xl leading-[1.0] font-medium tracking-tight mt-4">
              Fund restoration that listens to Country first.
            </h2>
            <p className="mt-6 text-[14.5px] lg:text-[15px] leading-relaxed opacity-90 max-w-2xl">
              SeedKeepers gives government departments, restoration funders, universities and project teams a
              permissioned view of Indigenous seed provenance. We co-design partnerships with Traditional Owner
              organisations and ranger groups, and the cultural authority always sits with them.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/register" data-testid="atlas-cta-register">
                <Button className="bg-[#FDFBF7] text-[#2C2A28] hover:bg-[#F4F1EA] gap-2 px-6">
                  Request partner access <ArrowUpRight className="w-4 h-4" />
                </Button>
              </Link>
              <a href="mailto:partners@seedkeepers.org" data-testid="atlas-cta-email">
                <Button variant="outline" className="bg-transparent border-white/30 text-[#FDFBF7] hover:bg-white/10 hover:text-[#FDFBF7] gap-2 px-6">
                  <Mail className="w-4 h-4" /> Email the team
                </Button>
              </a>
            </div>
          </div>

          <div className="text-[#FDFBF7]/90">
            <div className="grid gap-4 border-t border-white/20 pt-7">
              <Pledge title="Free, public viewing">Public provenance summaries remain open to government, universities and funders.</Pledge>
              <Pledge title="Cultural sovereignty">Restricted knowledge is never displayed. Permissions can be withdrawn at any time.</Pledge>
              <Pledge title="Benefit sharing">Commercial uses require explicit agreement and benefit-sharing terms with Traditional Owners.</Pledge>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Footer ---------- */}
      <footer className="bg-[#2C2A28] text-[#FDFBF7]/80">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-14 grid md:grid-cols-[1.4fr_1fr_1fr] gap-10">
          <div>
            <Brand variant="inline" tone="light" size={44} />
            <p className="text-[12.5px] leading-relaxed mt-5 max-w-md italic font-serif-display text-[#FDFBF7]/80">
              Indigenous provenance is not just scientific data. It includes Country, people, permission, ecological
              context, genetic resilience, climate future, cultural authority and restoration purpose.
            </p>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold opacity-70">Explore</div>
            <ul className="mt-4 space-y-2 text-[13px]">
              <li><a href="#map" className="hover:text-[#FDFBF7]">Map</a></li>
              <li><a href="#collections" className="hover:text-[#FDFBF7]">Public collections</a></li>
              <li><a href="#partners" className="hover:text-[#FDFBF7]">Partner with us</a></li>
              <li><Link to="/login" className="hover:text-[#FDFBF7]">Sign in</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold opacity-70">For Traditional Owners</div>
            <p className="mt-4 text-[12.5px] leading-relaxed opacity-90">
              Cultural authority remains with you. To list a provenance publicly or to withdraw consent at any time,
              contact your Traditional Owner Admin or our governance team.
            </p>
          </div>
        </div>
        <div className="border-t border-white/10 py-5">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex flex-wrap items-center justify-between gap-2 text-[11px] opacity-70">
            <div>© {new Date().getFullYear()} SeedKeepers. We acknowledge Traditional Owners of Country across these lands and waters.</div>
            <div className="tracking-[0.18em] uppercase">Restoration · Culture · Climate resilience</div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-display text-3xl lg:text-4xl font-bold tracking-tight text-[#FDFBF7]">
        {value == null ? "—" : value}
      </div>
      <div className="text-[10px] tracking-[0.22em] uppercase font-semibold mt-1 opacity-80">{label}</div>
    </div>
  );
}

function Pillar({ icon: Icon, title, body }) {
  return (
    <div className="rounded-md border border-[#E6E2D8] bg-white p-5">
      <div className="w-9 h-9 rounded-md bg-[#F4F1EA] border border-[#E6E2D8] flex items-center justify-center">
        <Icon className="w-4 h-4 text-[#8B5A2B]" strokeWidth={1.6} />
      </div>
      <div className="font-display font-semibold text-[15px] mt-4">{title}</div>
      <p className="text-[12.5px] leading-relaxed text-[#5C5854] mt-2">{body}</p>
    </div>
  );
}

function DomainCard({ icon: Icon, title, items, testid }) {
  return (
    <div className="rounded-md border border-[#E6E2D8] bg-white p-5 hover:shadow-md transition-all duration-200" data-testid={testid}>
      <div className="w-10 h-10 rounded-md bg-[#F4F1EA] border border-[#E6E2D8] flex items-center justify-center">
        <Icon className="w-4 h-4 text-[#8B5A2B]" strokeWidth={1.6} />
      </div>
      <h3 className="font-display font-semibold text-[15px] tracking-tight text-[#2C2A28] mt-4">{title}</h3>
      <ul className="mt-3 space-y-1.5 text-[12.5px] leading-relaxed text-[#5C5854]">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2">
            <span className="mt-[7px] w-1 h-1 rounded-full bg-[#8B5A2B] shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Pledge({ title, children }) {
  return (
    <div className="border-l-2 border-white/30 pl-4">
      <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#FDFBF7]">{title}</div>
      <p className="text-[12.5px] leading-relaxed opacity-90 mt-1">{children}</p>
    </div>
  );
}

function AtlasCard({ c, testid }) {
  const ai = c.ai_insights || {};
  const eco = c.ecological || {};
  const cli = c.climate || {};
  const res = c.restoration || {};
  const cul = c.cultural || {};
  return (
    <Card id={`c-${c.id}`} className="border-[#E6E2D8] shadow-sm bg-white hover:shadow-md transition-all overflow-hidden" data-testid={testid}>
      <div className="p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-mono-data text-[10px] text-[#8A857D]">{c.collection_id}</div>
            <div className="font-serif-display italic text-[20px] mt-1">{c.species_scientific_name || "Unidentified"}</div>
            <div className="text-[12.5px] text-[#5C5854]">{c.species_common_name}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#8A857D]">Confidence</div>
            <div className="font-mono-data text-xl text-[#2C2A28]">{ai.confidence_score ?? "—"}%</div>
          </div>
        </div>

        <p className="text-[13px] leading-relaxed text-[#2C2A28] mt-4 line-clamp-4">
          {ai.provenance_summary || "Public provenance summary not yet generated."}
        </p>

        <div className="grid grid-cols-2 gap-3 mt-5 text-[11.5px]">
          <Meta icon={Leaf} label="Family" value={c.plant_family} />
          <Meta icon={Users} label="TO group" value={cul.traditional_owner_group} />
          <Meta icon={MapPin} label="Climate zone" value={cli.current_climate_zone} />
          <Meta icon={Recycle} label="Restoration" value={res.suitable_project_types} />
        </div>
      </div>

      <div className="px-6 py-3 border-t border-[#E6E2D8] bg-[#FDFBF7] flex items-center justify-between">
        <div className="text-[11px] text-[#5C5854]">{c.organisation || "—"}</div>
        <Link to="/register" className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[#8B5A2B] underline-offset-4 hover:underline flex items-center gap-1">
          Request full record <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </Card>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-1.5">
      <Icon className="w-3.5 h-3.5 mt-0.5 text-[#8B5A2B]" strokeWidth={1.8} />
      <div>
        <div className="text-[9.5px] tracking-[0.18em] uppercase font-semibold text-[#8A857D]">{label}</div>
        <div className="text-[12px] text-[#2C2A28] mt-0.5">{value || <span className="text-[#8A857D]">—</span>}</div>
      </div>
    </div>
  );
}
